import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  TextField,
  Theme,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/styles';

import FormSection from '../../../components/form/FormSection';
import {
  MinimalLessonLocation,
  SwimmingLessonProviderWithLessonLocations,
  User,
} from '../../../types';
import { ExamDTO } from '../repository/ExamRepository';
import { Display } from '../views/CreateOrUpdateExam';
import SwimmingLessonProviderSelect from '../../admin/forms/SwimmingLessonProviderSelect';
import AppContext from '../../../AppContext';
import { arraysContainSameElements } from '../../../utils/common';
import { useSelector } from 'react-redux';
import { requireLessonLocationsForExam } from '../utils';

const useStyles = makeStyles((theme: Theme) => ({
  formField: {
    '&:not(:first-of-type)': {
      marginTop: theme.spacing(4),
    },
  },
  autocomplete: {
    '& .MuiAutocomplete-inputRoot': {
      paddingBottom: theme.spacing(1),
    },
  },
  clearButton: {
    marginLeft: theme.spacing(2),
  },
}));

interface SwimmingLessonProviderFormProps {
  swimmingLessonProvider: SwimmingLessonProviderWithLessonLocations | null;
  setSwimmingLessonProvider: Dispatch<
    SetStateAction<SwimmingLessonProviderWithLessonLocations | null>
  >;
  exam: ExamDTO;
  setExam: Dispatch<SetStateAction<ExamDTO>>;
  isNew: boolean;
  display: Display;
  setDisplay: Dispatch<SetStateAction<Display>>;
}

const SwimmingLessonProviderForm = (props: SwimmingLessonProviderFormProps) => {
  const {
    swimmingLessonProvider,
    setSwimmingLessonProvider,
    exam,
    setExam,
    isNew,
    display,
    setDisplay,
  } = props;

  const { roleViewManager } = useContext(AppContext);
  const account = useSelector(
    (selector: {
      user: {
        account: User;
      };
    }) => selector.user.account,
  );

  const examOnload = useRef<ExamDTO>(exam);

  const isAdmin = roleViewManager.isAdminView();
  const isSwimmingLessonProviderUser =
    roleViewManager.isSwimmingLessonProviderView() &&
    account.roles.includes('ROLE_SWIMMING_LESSON_PROVIDER_USER');
  const isLessonLocationUser =
    roleViewManager.isSwimmingLessonProviderView() &&
    account.roles.includes('ROLE_LESSON_LOCATION_USER');

  const hasPermissionsToEditLessonLocations = useMemo(() => {
    if (isAdmin || isSwimmingLessonProviderUser) {
      return true;
    }

    if (
      !isLessonLocationUser ||
      account?.grantedLessonLocations === undefined
    ) {
      return false;
    }

    if (isNew) {
      return true;
    }

    const grantedLessonLocationIds = account.grantedLessonLocations.map(
      (ll) => ll.id,
    );

    // require the lesson location user to be linked to all locations of the exam
    return (
      examOnload.current.lessonLocationIds.length >= 1 &&
      examOnload.current.lessonLocationIds.filter((id) =>
        grantedLessonLocationIds.includes(id),
      ).length === examOnload.current.lessonLocationIds.length
    );
  }, [
    isAdmin,
    isSwimmingLessonProviderUser,
    isLessonLocationUser,
    account,
    isNew,
    examOnload.current,
  ]);

  const determineLessonLocationOptions = useCallback(() => {
    if (
      !swimmingLessonProvider ||
      !requireLessonLocationsForExam(swimmingLessonProvider)
    ) {
      return [];
    }

    if (isAdmin || isSwimmingLessonProviderUser) {
      return swimmingLessonProvider.lessonLocations;
    }

    if (isLessonLocationUser) {
      const grantedLessonLocationsIds =
        account.grantedLessonLocations?.map((ll) => ll.id) || [];

      return swimmingLessonProvider.lessonLocations.filter((ll) =>
        grantedLessonLocationsIds.includes(ll.id),
      );
    }

    // in practice, this path should be unreachable
    return [];
  }, [
    swimmingLessonProvider,
    isLessonLocationUser,
    account.grantedLessonLocations,
  ]);

  const [lessonLocationOptions, setLessonLocationOptions] = useState<
    MinimalLessonLocation[]
  >(() => determineLessonLocationOptions());

  const [selectedLessonLocations, setSelectedLessonLocations] = useState<
    MinimalLessonLocation[]
  >(() => {
    if (
      !swimmingLessonProvider ||
      !requireLessonLocationsForExam(swimmingLessonProvider)
    ) {
      return [];
    }

    return swimmingLessonProvider.lessonLocations.filter((location) =>
      examOnload.current.lessonLocationIds.includes(location.id),
    );
  });

  const classes = useStyles();

  const handleSelect = (
    swimmingLessonProvider: SwimmingLessonProviderWithLessonLocations,
  ) => {
    setSwimmingLessonProvider(swimmingLessonProvider);
  };

  const handleClear = () => {
    setSwimmingLessonProvider(null);
  };

  const handleLessonLocationsChange = (
    _event: any,
    newValue: MinimalLessonLocation[],
  ) => {
    setSelectedLessonLocations(newValue);
  };

  useEffect(() => {
    if (!isNew) {
      return;
    }

    setExam((prev) => ({
      ...prev,
      swimmingLessonProviderId:
        swimmingLessonProvider !== null ? swimmingLessonProvider.id : '',
      examLocationId: '',
      lessonLocationIds: [],
      examinerId: '',
      diplomas: prev.diplomas.map((examLine) => ({
        ...examLine,
        diplomaTypeId: '',
        examinerId: '',
      })),
    }));
  }, [setExam, swimmingLessonProvider, isNew]);

  useEffect(() => {
    setExam((prev) => {
      const currentLessonLocationIds = prev.lessonLocationIds;
      const updatedLessonLocationIds = selectedLessonLocations.map((l) => l.id);

      let idsWereChanged = !arraysContainSameElements(
        currentLessonLocationIds,
        updatedLessonLocationIds,
      );

      // if location ids weren't changed, it's not necessary to clear the examiner and diploma type ids
      return idsWereChanged
        ? {
            ...prev,
            lessonLocationIds: updatedLessonLocationIds,
            examinerId: '',
            diplomas: prev.diplomas.map((examLine) => ({
              ...examLine,
              diplomaTypeId: '',
              examinerId: '',
            })),
          }
        : { ...prev };
    });
  }, [setExam, selectedLessonLocations]);

  useEffect(() => {
    if (swimmingLessonProvider) {
      setLessonLocationOptions(determineLessonLocationOptions());
    } else {
      setLessonLocationOptions([]);
      setSelectedLessonLocations([]);
    }
  }, [swimmingLessonProvider, determineLessonLocationOptions]);

  return (
    <>
      <FormSection title="Zwemlesaanbieder">
        <Box>
          {swimmingLessonProvider ? (
            <Box>
              {swimmingLessonProvider.name}
              {roleViewManager.isAdminView() && isNew && (
                <Button
                  className={classes.clearButton}
                  onClick={handleClear}
                  variant="contained"
                  color="secondary"
                  startIcon={
                    <FontAwesomeIcon icon={['fal', 'long-arrow-left']} />
                  }
                >
                  Bewerken
                </Button>
              )}
            </Box>
          ) : (
            <SwimmingLessonProviderSelect onSelect={handleSelect} />
          )}
          {swimmingLessonProvider && !swimmingLessonProvider.centralExams && (
            <Box className={classes.formField}>
              <FormControl fullWidth>
                <FormLabel id="exam-locations-select-label">
                  Deelnemende leslocatie(s)
                </FormLabel>
                <Box mt={2}>
                  <Autocomplete
                    multiple
                    className={classes.autocomplete}
                    onChange={handleLessonLocationsChange}
                    value={selectedLessonLocations}
                    options={lessonLocationOptions}
                    getOptionLabel={(option) => option.name}
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        /* eslint-disable-next-line react/jsx-props-no-spreading */
                        {...params}
                        fullWidth
                        placeholder="Selecteer leslocaties..."
                      />
                    )}
                    disabled={!hasPermissionsToEditLessonLocations}
                  />
                </Box>
              </FormControl>
            </Box>
          )}
        </Box>
        {display === 'minimal' && selectedLessonLocations.length >= 1 && (
          <Box mt={3}>
            <Button
              onClick={() => setDisplay('full')}
              size="large"
              variant="contained"
              color="primary"
            >
              Volgende stap
            </Button>
          </Box>
        )}
      </FormSection>
    </>
  );
};

export default SwimmingLessonProviderForm;
